import React, { useCallback, useMemo } from 'react';
import { Platform, Pressable, StyleSheet, Text, View } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { usePathname } from 'expo-router';
import { useResponsive } from '../../hooks/useResponsive';
import { useTier } from '../../context/TierContext';
import { useAuth } from '../../context/AuthContext';
import { resolveHeaderCTA } from './HeaderCTA';
import { calendarDaysUntil } from '../../utils/calendarDays';
import { useHomeSurveyActive } from '../survey/surveyVisibility';

const MS_PER_DAY = 1000 * 60 * 60 * 24;
// D-025 CBP tail-window: surface the floating card during the final 8 days of
// the 90-day pass. Matches HeaderCountdownChip's threshold.
const CBP_TAIL_WINDOW_MS = 8 * MS_PER_DAY;

// Window maths for trial / grandfather / CBP tail. Returns { totalDays, daysLeft, dayIndex } or null.
const resolveWindow = ({ subscriptionStatus, subscriptionId, proExpiresAt, trialStartedAt, trialCohortDays, grandfatherStartedAt, grandfatherExpiresAt }) => {
  const now = Date.now();
  // calendarDaysUntil aligns the boundary to local midnight so daysLeft
  // ticks down by exactly 1 each calendar day instead of locking on the
  // window-start hour-of-day for ~24 hours.
  if (subscriptionStatus === 'trial_active' && trialStartedAt && trialCohortDays) {
    const start = new Date(trialStartedAt).getTime();
    const totalDays = trialCohortDays;
    const end = new Date(start + totalDays * MS_PER_DAY);
    const daysLeft = calendarDaysUntil(end) ?? 0;
    const dayIndex = Math.min(totalDays, Math.max(1, totalDays - daysLeft + 1));
    return { kind: 'trial', totalDays, daysLeft, dayIndex };
  }
  if (subscriptionStatus === 'grandfather_active' && grandfatherExpiresAt) {
    const end = new Date(grandfatherExpiresAt).getTime();
    // Fallback window length when grandfather_started_at is missing. Matches the
    // migration constant GRANDFATHER_DAYS=14 (reduced from 30 on 2026-04-21).
    const start = grandfatherStartedAt
      ? new Date(grandfatherStartedAt).getTime()
      : end - 14 * MS_PER_DAY;
    const totalDays = Math.max(1, Math.round((end - start) / MS_PER_DAY));
    const daysLeft = calendarDaysUntil(end) ?? 0;
    const dayIndex = Math.min(totalDays, Math.max(1, totalDays - daysLeft + 1));
    return { kind: 'grandfather', totalDays, daysLeft, dayIndex };
  }
  // D-025 CBP tail-window: only show in the final 8 days of the 90-day pass.
  if (subscriptionStatus === 'active' && !subscriptionId && proExpiresAt) {
    const end = new Date(proExpiresAt).getTime();
    const remainingMs = end - now;
    if (remainingMs > 0 && remainingMs <= CBP_TAIL_WINDOW_MS) {
      const totalDays = 8;
      const daysLeft = calendarDaysUntil(proExpiresAt) ?? 0;
      const dayIndex = Math.min(totalDays, Math.max(1, totalDays - daysLeft + 1));
      return { kind: 'cbp', totalDays, daysLeft, dayIndex };
    }
  }
  return null;
};

const titleForKind = (kind, daysLeft) => {
  if (kind === 'trial') return `${daysLeft} days left of Pro features`;
  if (kind === 'grandfather') return `${daysLeft} days left on your Founder's Rate`;
  if (kind === 'cbp') return `${daysLeft} days left on your Pass`;
  return null;
};

const headForKind = (kind) => {
  if (kind === 'trial') return 'Pro trial';
  if (kind === 'grandfather') return "Founder's rate";
  if (kind === 'cbp') return "Car Buyer's Pass";
  return null;
};

const subForKind = (kind) => {
  if (kind === 'trial') {
    return null;
  }
  if (kind === 'grandfather') {
    return 'Lock in the discounted rate before the window closes.';
  }
  if (kind === 'cbp') {
    return 'Renew for another 90 days, or switch to monthly to keep Pro without a gap.';
  }
  return 'Unlock full match scores, running costs and negotiations on every listing.';
};

const DesktopProFloatingCard = () => {
  const { isMobile, screenWidth } = useResponsive();
  const tier = useTier();
  const { user } = useAuth();
  const pathname = usePathname();
  const surveyActive = useHomeSurveyActive();

  const ctaSpec = useMemo(
    () => resolveHeaderCTA(tier.subscriptionStatus, tier.proExpiresAt, tier.hasProAccess),
    [tier.subscriptionStatus, tier.proExpiresAt, tier.hasProAccess]
  );

  const window = useMemo(
    () =>
      resolveWindow({
        subscriptionStatus: tier.subscriptionStatus,
        subscriptionId: tier.subscriptionId,
        proExpiresAt: tier.proExpiresAt,
        trialStartedAt: tier.trialStartedAt,
        trialCohortDays: tier.trialCohortDays,
        grandfatherStartedAt: tier.grandfatherStartedAt,
        grandfatherExpiresAt: tier.grandfatherExpiresAt,
      }),
    [tier.subscriptionStatus, tier.subscriptionId, tier.proExpiresAt, tier.trialStartedAt, tier.trialCohortDays, tier.grandfatherStartedAt, tier.grandfatherExpiresAt]
  );

  // D-025: CBP users hit the default HeaderCTA null branch (subscription_status='active')
  // so resolveHeaderCTA returns null. When the CBP tail window is active, synthesize
  // a CTA spec so the floating card still renders with renewal framing.
  const effectiveCtaSpec = ctaSpec || (window?.kind === 'cbp'
    ? { label: 'Renew your Pass', source: 'cbp_header_chip' }
    : null);

  const handleCTAPress = useCallback(() => {
    if (!effectiveCtaSpec) return;
    const payload = { source: effectiveCtaSpec.source };
    if (window?.kind === 'cbp') payload.preselectedPricingOption = 'car_buyers_pass';
    tier.triggerUpgradeModal(payload);
  }, [effectiveCtaSpec, window?.kind, tier]);

  // Step 8: never surface the upgrade card for a carded user (card-on-file,
  // full Pro, pre day-7 charge). resolveHeaderCTA already returns null for
  // trial_carded, so effectiveCtaSpec is null and this is belt-and-suspenders
  // against any future synthesized CTA spec.
  if (tier.subscriptionStatus === 'trial_carded') return null;

  // Only surface on /home per product decision 2026-04-20.
  // Step aside when a survey is occupying the bottom-right slot (the survey
  // sits "in place of" the upgrade card).
  // Hide on narrow viewports (< 1400px): below this the right gutter is too
  // small for the card to sit beside the 940px feed column without crowding it.
  if (isMobile || screenWidth < 1400 || tier.loading || !effectiveCtaSpec || !user || pathname !== '/home' || surveyActive) return null;

  const progressPct = window ? Math.max(0, Math.min(100, ((window.totalDays - window.daysLeft) / window.totalDays) * 100)) : 0;

  return (
    <View style={styles.anchor} pointerEvents="box-none">
      <View style={styles.card}>
        {Platform.OS !== 'web' && (
          <LinearGradient
            colors={['rgba(20, 76, 184, 0.18)', 'rgba(20, 76, 184, 0.06)']}
            start={{ x: 0, y: 0 }}
            end={{ x: 0, y: 1 }}
            style={StyleSheet.absoluteFill}
            pointerEvents="none"
          />
        )}

        {window ? (
          <>
            <View style={styles.head}>
              <MaterialCommunityIcons name="star-four-points" size={12} color="#8FB3FF" />
              <Text style={styles.headText}>{headForKind(window.kind)}</Text>
            </View>
            <Text style={styles.title}>{titleForKind(window.kind, window.daysLeft)}</Text>
            {subForKind(window.kind) ? <Text style={styles.sub}>{subForKind(window.kind)}</Text> : null}
            <View style={styles.bar}>
              <LinearGradient
                colors={['#538AF9', '#2466ED']}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 0 }}
                style={[styles.barFill, { width: `${progressPct}%` }]}
              />
            </View>
            <View style={styles.barLabelRow}>
              {window.kind === 'cbp' ? (
                <Text style={styles.barLabel}>Final stretch of your Pass</Text>
              ) : (
                <Text style={styles.barLabel}>
                  {window.totalDays - window.daysLeft} of {window.totalDays} elapsed
                </Text>
              )}
              <Text style={styles.barLabel}>{window.daysLeft} left</Text>
            </View>
          </>
        ) : (
          <>
            <View style={styles.head}>
              <MaterialCommunityIcons name="star-four-points" size={12} color="#8FB3FF" />
              <Text style={styles.headText}>Upgrade</Text>
            </View>
            <Text style={styles.title}>Unlock the full experience</Text>
            <Text style={styles.sub}>{subForKind(null)}</Text>
          </>
        )}

        <Pressable onPress={handleCTAPress} style={styles.ctaWrapper} accessibilityRole="button" accessibilityLabel={effectiveCtaSpec.label}>
          <LinearGradient
            colors={['#538AF9', '#2466ED']}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={styles.cta}
          >
            <Text style={styles.ctaText}>{ctaLabelFor(effectiveCtaSpec, window)}</Text>
          </LinearGradient>
        </Pressable>

      </View>
    </View>
  );
};

const ctaLabelFor = (ctaSpec, window) => {
  if (window?.kind === 'trial') return 'Unlock All';
  if (window?.kind === 'grandfather') return "Lock in Founder's Rate";
  if (window?.kind === 'cbp') return 'Renew your Pass';
  return ctaSpec.label;
};

const styles = StyleSheet.create({
  // `position: 'fixed'` is web-only; Platform guard keeps native from receiving it.
  anchor: {
    // Web: size to the right gutter so the card never overlaps the 940px-wide
    // centered /home feed column. Gutter = (100vw - 940)/2; subtract the 24px
    // viewport inset + a 16px gap from the content edge. Capped at 300 so it
    // never grows wider than the original design on very wide screens.
    ...(Platform.OS === 'web'
      ? { position: 'fixed', width: 'calc((100vw - 940px) / 2 - 40px)', maxWidth: 300 }
      : { position: 'absolute', width: 300 }),
    bottom: 24,
    right: 24,
    zIndex: 200,
  },
  card: {
    position: 'relative',
    padding: 16,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: 'rgba(83, 138, 249, 0.22)',
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.35,
    shadowRadius: 24,
    elevation: 10,
    // Web: composited radial (top layer) + linear (base) exactly matching the design's
    // `.pro-card` background + `.pro-card::before` radial overlay. Native uses the
    // LinearGradient child above as a fallback.
    ...(Platform.OS === 'web'
      ? {
          backgroundImage:
            'radial-gradient(200px 100px at 100% 0%, rgba(83,138,249,0.2), transparent 60%), linear-gradient(180deg, rgba(20,76,184,0.18) 0%, rgba(20,76,184,0.06) 100%)',
        }
      : {}),
  },
  head: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  headText: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 11,
    letterSpacing: 1,
    color: '#8FB3FF',
    textTransform: 'uppercase',
  },
  title: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 15,
    lineHeight: 20,
    color: '#FFFFFF',
    marginTop: 8,
  },
  sub: {
    fontFamily: 'Satoshi-Regular',
    fontSize: 12,
    lineHeight: 18,
    color: 'rgba(255,255,255,0.6)',
    marginTop: 4,
  },
  bar: {
    height: 4,
    borderRadius: 99,
    backgroundColor: 'rgba(255,255,255,0.08)',
    marginTop: 12,
    overflow: 'hidden',
  },
  barFill: {
    height: '100%',
    borderRadius: 99,
  },
  barLabelRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 8,
  },
  barLabel: {
    fontFamily: 'Satoshi-Regular',
    fontSize: 11,
    color: 'rgba(255,255,255,0.55)',
  },
  ctaWrapper: {
    marginTop: 14,
  },
  cta: {
    paddingVertical: 10,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: 'rgba(20,76,184,0.35)',
    shadowOffset: { width: 0, height: 5 },
    shadowOpacity: 1,
    shadowRadius: 14,
  },
  ctaText: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 13,
    color: '#FFFFFF',
    letterSpacing: -0.1,
  },
});

export default DesktopProFloatingCard;
